import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BackLink } from "@/components/BackLink";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, BookOpen, Download, Loader2, Search } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Per-handbook accent styling
const THEMES = {
  admin: {
    accent: "text-amber-400",
    accentBg: "bg-amber-500",
    accentText: "text-slate-950",
    accentHover: "hover:bg-amber-400",
  },
  coach: {
    accent: "text-emerald-400",
    accentBg: "bg-emerald-500",
    accentText: "text-slate-950",
    accentHover: "hover:bg-emerald-400",
  },
  parent: {
    accent: "text-sky-400",
    accentBg: "bg-sky-500",
    accentText: "text-slate-950",
    accentHover: "hover:bg-sky-400",
  },
};

const BACK_LINK = {
  admin: "/admin",
  coach: "/academy/coach-dashboard",
  parent: "/academy/parent-dashboard",
};

export default function HandbookReader({ kind: kindProp }) {
  const params = useParams();
  const kind = kindProp || params.kind || "admin";
  const theme = THEMES[kind] || THEMES.admin;
  const backLink = BACK_LINK[kind] || "/";

  const [meta, setMeta] = useState({ title: "Handbook", subtitle: "" });
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/academy/handbook/${kind}`, { withCredentials: true })
      .then((r) => {
        setContent(r.data.content || "");
        setUpdatedAt(r.data.updated_at);
        setMeta({ title: r.data.title, subtitle: r.data.subtitle });
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load handbook"))
      .finally(() => setLoading(false));
  }, [kind]);

  const downloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/academy/handbook/${kind}/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const niceKind = kind.charAt(0).toUpperCase() + kind.slice(1);
      a.download = `SCC_Academy_${niceKind}_Handbook_${date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate PDF");
    }
  };

  const jumpToMatch = () => {
    if (!query.trim()) return;
    const el = document.querySelector(".handbook-content");
    if (!el) return;
    const range = document.createRange();
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.textContent.toLowerCase().includes(query.toLowerCase())) {
        range.selectNode(node);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        node.parentElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
    toast.info("No match found");
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5 sticky top-0 z-10 print:hidden">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <BackLink to={backLink} label="Back" className="text-white"
              testId="back-btn" />
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BookOpen className={`w-6 h-6 ${theme.accent}`} />
              {meta.title}
            </h1>
            {updatedAt && (
              <span className="text-xs text-slate-500 hidden md:inline">
                Updated {new Date(updatedAt).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && jumpToMatch()}
                placeholder={`Search ${kind} handbook...`}
                className="pl-9 bg-slate-900 border-slate-700 text-white w-56"
                data-testid="handbook-search"
              />
            </div>
            <Button
              onClick={downloadPdf}
              size="sm"
              className={`${theme.accentBg} ${theme.accentText} ${theme.accentHover}`}
              data-testid="download-handbook-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button
              onClick={() => window.print()}
              variant="outline"
              size="sm"
              className="border-blue-500/40 text-blue-400"
              data-testid="print-handbook-btn"
            >
              Print
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {loading && (
          <div className="py-16 flex justify-center">
            <Loader2 className={`w-10 h-10 animate-spin ${theme.accent}`} />
          </div>
        )}

        {!loading && (
          <Card className="p-6 md:p-10 bg-slate-900/40 print:bg-white print:text-black handbook-content" data-testid={`handbook-content-${kind}`}>
            <div className="prose prose-invert prose-slate max-w-none print:prose prose-headings:text-white prose-p:text-slate-300 prose-strong:text-white prose-code:text-amber-300 prose-th:text-white prose-td:text-slate-300 print:prose-headings:text-black print:prose-p:text-slate-800 print:prose-strong:text-black print:prose-code:text-emerald-700 print:prose-th:text-black print:prose-td:text-slate-700">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
